import React from 'react';

import styled from '@emotion/styled';
import { radius } from '@lightspeed/cirrus-tokens/utils';
import { Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { DropdownContainer, DropdownContent } from '@lightspeed/flame/Dropdown';
import { usePopper, useToggle, useOnClickOutside, useEventListener } from '@lightspeed/flame/hooks';
import { IconMathAdd } from '@lightspeed/flame/Icon/MathAdd';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import { IconSmallChevronUp } from '@lightspeed/flame/Icon/SmallChevronUp';

import { getIdentity } from 'root/api/identity/identity';
import Monitoring from 'shared_modules/Monitoring';

import { IdentityObserver } from './identity-observer.ts';

const SUPPORTED_PRODUCTS = [
  {
    key: 'retail',
    name: 'Retail',
    active: true,
  },
  {
    key: 'golf',
    name: 'Golf',
  },
];

const SwitcherContainer = styled('div')`
  -webkit-font-smoothing: antialiased;
  line-height: 1.125rem;
  position: relative;

  .dropdown-container {
    width: calc(240px - 1.5rem);
    background-color: #2f3137;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0.1875rem;
    border-bottom-right-radius: 0.1875rem;
    border: 0;
    padding: 0;
    box-shadow: none;
    transition: none;
  }
`;

const ProductSwitcher = styled('button')`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 2.5rem;
  margin: 0;
  cursor: pointer;
  color: #ffffff;
  background-color: ${props => (props.isActive ? '#2e61de' : '#2f3137')};
  user-select: none;
  border: none;
  border-top-left-radius: 0.1875rem;
  border-top-right-radius: 0.1875rem;
  border-bottom-left-radius: ${props => (props.isActive ? 0 : '0.1875rem')};
  border-bottom-right-radius: ${props => (props.isActive ? 0 : '0.1875rem')};
  padding: 0 0.75rem;
  line-height: 1.125rem;
  font-weight: 700 !important;

  pointer-events: ${props => (props.isDisabled ? 'none' : 'auto')};

  &:hover {
    background-color: ${props => (props.isActive ? '#2e61de' : '#464a51')};
  }
`;

const DropdownList = styled('ul')`
  margin: 0;
  padding: 0;
`;

const DropdownItem = styled('li')`
  list-style-type: none;
  margin: 0;
  padding: 0;

  .dropdown-item-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 2.5rem;
    padding: 0 0.75rem;
    text-decoration: none;
    color: #fff;
    font-weight: 700 !important;
    font-size: 16px;

    &:hover {
      background-color: #464a51;
    }
  }

  .dashboard-link {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    font-size: 0.875rem;
    color: #fff;
    padding: 0.75rem;
    border-bottom-left-radius: 0.1875rem;
    border-bottom-right-radius: 0.1875rem;
    font-weight: normal !important;
  }

  .dashboard-link-info {
    color: #8b939b;
    font-size: 0.75rem;
  }
`;

const DropdownMessage = styled('div')`
  font-size: 0.875rem;
  padding: 0.75rem;

  p {
    color: #a3abb3;
    margin: 0;
    padding: 0;
  }
`;

const initialUserIdentity = { isLSIDUser: false, products: [], grants: [], features: [] };

const formatUserIdentity = (domain, identityData) => {
  const grants = Object.keys(identityData?.grants?.user || {});
  const features = identityData?.features || [];
  const jti = identityData?.jti;
  const initialJti = identityData?.initialJti;

  if (!grants.length || !jti) {
    return initialUserIdentity;
  }

  const products = [];

  SUPPORTED_PRODUCTS.filter(product => grants.includes(product.key)).forEach(product => {
    products.push({
      ...product,
      url: `${domain}/auth/lightspeed/${product.key}`,
    });
  });

  return { isLSIDUser: true, products, grants, features, initialJti };
};

export const LsidProductSwitcher = ({
  domain,
  appDomain,
  clientId,
  showEcom,
  ecomLink,
  showEcomLite,
  ecomLiteLink,
  navigate,
}) => {
  // Setup Custom Dropdown
  const targetRef = React.createRef();
  const popperRef = React.createRef();
  usePopper(targetRef, popperRef, {
    placement: 'bottom-end',
  });
  const { toggle, isActive } = useToggle();
  useOnClickOutside(popperRef, () => isActive && toggle());
  useEventListener(event => {
    if (event.key === 'Escape' && isActive) {
      toggle();
    }
  }, 'keyup');

  // Load available products and features
  const [userIdentity, setUserIdentity] = React.useState(initialUserIdentity);
  const [retailUserID, setRetailUserID] = React.useState();
  const { isLSIDUser, products, grants, features, initialJti } = userIdentity;

  // Deactivate dropdown functionality when not an LSID user and no access to eCom
  const isDropdownDisabled = !isLSIDUser && !showEcom;

  // Will determine if we show the LSID Dashboard link
  const isShowLsidInvite = products.length === 1 && products[0].key === 'retail' && !showEcom;

  // Only pull the first eCom grant, as this will cover most of our use cases.
  const eComGrant = grants.find(grant => grant.startsWith('ecom'));

  // For the identity change subcription
  const [identityObserver, setIdentityObserver] = React.useState();

  // To enable the observer
  const isIdentityObserverEnabled =
    isLSIDUser && features?.includes('lsid_retail_identity_auto_update');

  // Gets the Retail user ID to compare against the LSID grants
  React.useEffect(() => {
    window.merchantos.session.init().done(response => {
      const session = window.merchantos.session.sessData;
      setRetailUserID(session.systemUserID);
    });
  }, []);

  // Initialize the identity observer
  React.useEffect(() => {
    const observer = new IdentityObserver({
      lsidIdentityServiceUrl: domain,
    });

    setIdentityObserver(observer);

    return function cleanup() {
      observer.cleanupWatchers();
    };
  }, [domain]);

  // Initialize call to identity service
  React.useEffect(() => {
    const fetchData = async () => {
      if (identityObserver) {
        let identityData;

        try {
          const identityResponse = await identityObserver.fetchIdentity();
          identityData = identityResponse.data;
        } catch (err) {
          Monitoring.NRError(err, 'LsidProductSwitcher');
        }

        setUserIdentity(formatUserIdentity(domain, identityData));
      }
    };

    fetchData();
  }, [domain, identityObserver]);

  // Subscribe to identity observer events
  React.useEffect(() => {
    let unsubscribeIdentityObserver;

    if (identityObserver && isIdentityObserverEnabled && retailUserID) {
      unsubscribeIdentityObserver = identityObserver.subscribe(event => {
        switch (event.type) {
          case 'UPDATED':
            if (
              event.identity.initialJti === initialJti &&
              event.identity.grants?.user?.retail?.includes(retailUserID)
            ) {
              setUserIdentity(formatUserIdentity(domain, event.identity));
            } else {
              window.merchantos.utils.openUrl('/login.html');
            }
            break;
          case 'UNAUTHORIZED':
            window.merchantos.utils.openUrl('/auth/logout');
            break;
          case 'ERROR':
            Monitoring.NRError(event.error, 'LsidProductSwitcher');
            break;
          default:
            Monitoring.NREvent('lsid.observer_event_undefined', 'LsidProductSwitcher');
            break;
        }
      });
    }

    return function cleanup() {
      if (unsubscribeIdentityObserver) {
        unsubscribeIdentityObserver();
      }
    };
  }, [domain, identityObserver, isIdentityObserverEnabled, retailUserID, initialJti]);

  // eCom link click logic
  const handleEcomClick = e => {
    toggle();

    if (eComGrant) {
      window.merchantos.utils.openUrl(`${domain}/auth/lightspeed/${eComGrant}`, 'ecom');
      e.preventDefault();
    } else {
      navigate({
        e,
        action: () =>
          (window.ecomLink || ecomLink) === '/ecom/provisioning'
            ? window.PageRouter.goTo(ecomLink)
            : window.merchantos.utils.openUrl(ecomLink, 'ecom'),
      });
    }
  };

  return (
    <SwitcherContainer data-test="lsid-product-switcher">
      <ProductSwitcher
        type="button"
        ref={targetRef}
        onClick={isDropdownDisabled ? () => {} : toggle}
        isActive={isActive}
        isDisabled={isDropdownDisabled}
      >
        <Box>Retail</Box>
        {!isDropdownDisabled && (
          <Box marginTop="-2px">{isActive ? <IconSmallChevronUp /> : <IconSmallChevronDown />}</Box>
        )}
      </ProductSwitcher>
      {isActive && (
        <DropdownContainer
          ref={popperRef}
          isActive={isActive}
          zIndex={100}
          className="dropdown-container"
        >
          {showEcomLite && (
            <React.Fragment>
              <DropdownContent m={0}>
                <DropdownList>
                  <DropdownItem className="ecomLite">
                    <a
                      href={ecomLiteLink}
                      className="dropdown-item-link _flex-layout"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Online
                    </a>
                  </DropdownItem>
                </DropdownList>
              </DropdownContent>
              <Divider color="#464A51" />
            </React.Fragment>
          )}

          {(showEcom || eComGrant) && (
            <DropdownContent m={0}>
              <DropdownList>
                <DropdownItem>
                  <a href={ecomLink} className="dropdown-item-link" onClick={handleEcomClick}>
                    eCom
                  </a>
                </DropdownItem>
              </DropdownList>
            </DropdownContent>
          )}

          {!!products.length && (
            <React.Fragment>
              <DropdownContent m={0}>
                <DropdownList>
                  {products.map(
                    product =>
                      !product.active && (
                        <DropdownItem key={product.key}>
                          <a
                            href={product.url}
                            target={product.key}
                            className="dropdown-item-link"
                            data-test={`lsid-product-link-${product.key}`}
                            onClick={e =>
                              navigate({
                                e,
                                action: () => {
                                  toggle();
                                  window.merchantos.utils.openUrl(product.url, product.key);
                                },
                              })
                            }
                          >
                            {product.name}
                          </a>
                        </DropdownItem>
                      )
                  )}
                </DropdownList>
              </DropdownContent>
            </React.Fragment>
          )}

          {isShowLsidInvite && (
            <React.Fragment>
              <Divider color="#464A51" />
              <DropdownContent m={0}>
                <DropdownMessage>
                  <p data-test="lsid-product-switcher-message">
                    More than one Lightspeed account? Link your accounts to enable one-step
                    switching.
                  </p>
                </DropdownMessage>
              </DropdownContent>
            </React.Fragment>
          )}

          <React.Fragment>
            <Divider color="#464A51" />
            <DropdownContent m={0}>
              <DropdownList>
                <DropdownItem className="dropdown-item">
                  <a
                    href={appDomain}
                    target="lsid"
                    className="dropdown-item-link dashboard-link"
                    onClick={e =>
                      navigate({
                        e,
                        action: () => {
                          toggle();
                          const language = window.Localize ? window.Localize.getLanguage() : 'en';
                          const url = new URL(
                            `/link-account?lang=${language}`,
                            appDomain
                          ).toString();
                          window.merchantos.utils.openUrl(url, 'lsid');
                        },
                      })
                    }
                  >
                    <span>
                      Link accounts <br />
                      <span className="dashboard-link-info">Link another Lightspeed account</span>
                    </span>
                    <IconMathAdd />
                  </a>
                </DropdownItem>
              </DropdownList>
            </DropdownContent>
          </React.Fragment>
        </DropdownContainer>
      )}
    </SwitcherContainer>
  );
};
